import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useTranslation, Box, useTokens } from '@gemini/core';
import { ResponsiveContainer, LineChart as LineChart$1, Line } from 'recharts';
import { useId, useState, useRef, useEffect } from 'react';
import { InfoState } from '@gemini/ui';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { ChartHeader, ChartLegend, useDynamicAxisSize, useChartGrid, useChartAxis, useChartTooltip } from '@gemini/ui/unsafe';

const LINE_CHART_I18N = {
    'en-GB': {
        'voiceover::warning': "If you're using VoiceOver on a Mac, you will need to turn QuickNav off in order to navigate our charts."
    },
    'fr-FR': {
        'voiceover::warning': 'Si vous utilisez VoiceOver sur un Mac, vous devrez désactiver QuickNav pour parcourir nos diagrammes.'
    },
    'de-DE': {
        'voiceover::warning': 'Wenn Sie VoiceOver auf einem Mac verwenden, müssen Sie QuickNav ausschalten, um in unseren Diagrammen navigieren zu können.'
    },
    'nl-NL': {
        'voiceover::warning': 'Als u VoiceOver op een Mac gebruikt, moet u QuickNav uitschakelen om door onze grafieken te kunnen navigeren.'
    }
};
const useLineChartTranslation = ()=>useTranslation(LINE_CHART_I18N);

const PRESET_COLORS = [
    'categorical.1',
    'categorical.2',
    'categorical.3',
    'categorical.4',
    'categorical.5'
];
const LineChart = ({ 'aria-label': ariaLabel, 'aria-description': ariaDescription, data, dataTemplate, emptyState, dotVisibility, height, aspectRatio, headerProps, legendProps, showLegend = true, axisProps, showAxis, showGrid, ...containerBoxProps })=>{
    const chartId = useId();
    const showChart = data.length > 0;
    const { pois: templatePois, formatPoiValue, formatPoiCategory } = dataTemplate;
    const poiKeys = Object.keys(templatePois);
    const pois = showChart ? poiKeys.map((key, index)=>({
            key,
            label: templatePois[key].label,
            color: templatePois[key].color ?? PRESET_COLORS[index % PRESET_COLORS.length] ?? PRESET_COLORS[0]
        })) : [];
    const [legendId, setLegendId] = useState(undefined);
    const legendRef = useRef(null);
    const [activePois, setActivePois] = useState(legendProps?.defaultValue || poiKeys);
    const { title: headerTitle, subtitle: headerSubtitle, children: headerChildren, ...restHeaderProps } = headerProps || {};
    useEffect(()=>{
        if (legendProps === null) {
            setLegendId(undefined);
            return;
        }
        if (!legendRef.current) return;
        setLegendId(legendRef.current.id);
    }, [
        legendProps
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        as: "section",
        width: "100%",
        ...containerBoxProps,
        children: [
            headerTitle || headerSubtitle || headerChildren ? /*#__PURE__*/ jsx(ChartHeader, {
                ...restHeaderProps,
                chartId: chartId,
                title: headerTitle,
                subtitle: headerSubtitle,
                marginBottom: "spacing.32",
                children: headerChildren
            }) : null,
            showChart ? /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx(LineChartApplication, {
                        chartId: chartId,
                        title: ariaLabel,
                        desc: ariaDescription,
                        legendId: legendId,
                        data: data,
                        pois: pois,
                        activePois: activePois,
                        formatPoiValue: formatPoiValue,
                        formatPoiCategory: formatPoiCategory,
                        isLive: legendProps?.isInteractive || !!headerChildren,
                        showGrid: showGrid,
                        showAxis: showAxis,
                        axisProps: axisProps,
                        height: height,
                        aspectRatio: aspectRatio,
                        dotVisibility: dotVisibility
                    }),
                    showLegend ? /*#__PURE__*/ jsx(ChartLegend, {
                        containerRef: legendRef,
                        chartId: chartId,
                        marginTop: "spacing.8",
                        data: pois,
                        ...legendProps?.isInteractive ? {
                            value: activePois,
                            onChange: (value)=>{
                                setActivePois(value);
                                legendProps?.onChange?.(value);
                            }
                        } : {}
                    }) : null
                ]
            }) : /*#__PURE__*/ jsx(Box, {
                display: "flex",
                justifyContent: "center",
                children: /*#__PURE__*/ jsx(InfoState, {
                    ...emptyState
                })
            })
        ]
    });
};
const LineChartApplication = ({ chartId, title, desc, legendId, data, pois, activePois, formatPoiValue, formatPoiCategory, isLive, height, aspectRatio = 2 / 1, axisProps, showAxis = true, showGrid = true, dotVisibility })=>{
    const t = useLineChartTranslation();
    const warningId = useId();
    const titleId = useId();
    const descriptionId = useId();
    const tokens = useTokens();
    const { axisSize, setChartRef } = useDynamicAxisSize({
        data,
        direction: 'horizontal'
    });
    const { render: renderChartGrid } = useChartGrid({
        isVisible: showGrid !== false,
        showVerticalLines: showGrid === true || showGrid === 'vertical',
        showHorizontalLines: showGrid === true || showGrid === 'horizontal'
    });
    const { render: { titleX: renderChartAxisXTitle, axisX: renderChartAxisX, titleY: renderChartAxisYTitle, axisY: renderChartAxisY } } = useChartAxis({
        ...axisProps,
        dataKey: 'group',
        isVisible: showAxis,
        formatterCategory: formatPoiCategory ? (value)=>formatPoiCategory(value) : undefined,
        formatterValue: formatPoiValue ? (value)=>formatPoiValue(value) : undefined,
        chartType: 'line'
    });
    const { render: renderChartTooltip } = useChartTooltip({
        chartType: 'line',
        formatter: formatPoiValue
    });
    return /*#__PURE__*/ jsxs(Box, {
        width: "100%",
        height: height,
        children: [
            /*#__PURE__*/ jsx(VisuallyHidden, {
                id: warningId,
                children: t('voiceover::warning')
            }),
            renderChartAxisYTitle(),
            /*#__PURE__*/ jsx(ResponsiveContainer, {
                width: "100%",
                height: "100%",
                ...!height ? {
                    aspect: aspectRatio
                } : {},
                children: /*#__PURE__*/ jsxs(LineChart$1, {
                    ref: setChartRef,
                    id: chartId,
                    data: data,
                    accessibilityLayer: true,
                    "aria-labelledby": titleId,
                    "aria-describedby": `${warningId} ${descriptionId} ${legendId}`,
                    "aria-live": isLive ? 'polite' : undefined,
                    margin: {
                        top: 10,
                        bottom: 0,
                        left: 0,
                        right: 0
                    },
                    children: [
                        /*#__PURE__*/ jsx("title", {
                            id: titleId,
                            children: title
                        }),
                        /*#__PURE__*/ jsx("desc", {
                            id: descriptionId,
                            children: desc
                        }),
                        renderChartGrid(),
                        renderChartAxisX({
                            height: axisSize?.xAxisHeight
                        }),
                        renderChartAxisY({
                            width: axisSize?.yAxisWidth
                        }),
                        renderChartTooltip(),
                        pois.map((poi)=>/*#__PURE__*/ jsx(Line, {
                                dataKey: `pois.${poi.key}`,
                                name: poi.label,
                                stroke: tokens[`color.surface.data.${poi.color}`],
                                hide: !activePois.includes(poi.key),
                                strokeWidth: 2,
                                dot: {
                                    r: dotVisibility === 'always' ? 4 : 0,
                                    strokeWidth: 0,
                                    fill: tokens[`color.surface.data.${poi.color}`]
                                },
                                activeDot: {
                                    r: 4,
                                    strokeWidth: 2,
                                    stroke: tokens[`color.surface.data.${poi.color}`],
                                    fill: tokens['color.surface.default.default']
                                }
                            }, poi.key))
                    ]
                })
            }),
            renderChartAxisXTitle()
        ]
    });
};

export { LineChart };
//# sourceMappingURL=index.mjs.map
